import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import { eq, useLiveQuery } from "@tanstack/react-db";
import { useMemo, useRef } from "react";
import { useMarkReadSupportTicketAction } from "../actions/use-support-ticket-actions";
import { useGlobalSupportTicketCollections } from "../providers/global-support-ticket-collections-context";

/**
 * Hook that automatically marks a conversation as read when it's first viewed.
 * Uses useMemo to ensure the side effect only runs once per conversation.
 * Tracks the last processed conversation ID to handle navigation between conversations.
 */
export function useMarkSupportTicketAsRead(
  supportTicketId: SupportTicketId,
): void {
  const markReadAction = useMarkReadSupportTicketAction();
  const lastSupportTicketIdRef = useRef<SupportTicketId | null>(null);
  const collections = useGlobalSupportTicketCollections();
  const conversation = useLiveQuery(
    (q) =>
      q
        .from({ c: collections.supportTicketsList.collection })
        .where(({ c }) => eq(c.id, supportTicketId))
        .findOne(),
    [supportTicketId],
  ).data;

  useMemo(() => {
    if (
      conversation &&
      conversation.readAt === null &&
      lastSupportTicketIdRef.current !== supportTicketId
    ) {
      void markReadAction.execute(supportTicketId);
      lastSupportTicketIdRef.current = supportTicketId;
    }
  }, [conversation, supportTicketId, markReadAction]);
}
